import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import apiClient from '@/lib/apiClient';
import { HTTP_STATUS_UNAUTHORIZED } from '@/lib/constants';

// ===== Module-level helpers — stable identities =====

const onLoadError = (err, onUnauthorized) => {
  if (err.response?.status === HTTP_STATUS_UNAUTHORIZED) {
    toast.error('Session expired. Please login again.');
    onUnauthorized();
  } else {
    toast.error('Failed to load assessments');
  }
};

const fetchAssessments = (args) => {
  const params = {};
  if (args.search) params.search = args.search;
  if (args.filterStability) params.stability_level = args.filterStability;
  if (args.filterStatus) params.lead_status = args.filterStatus;
  return apiClient.get('/admin/assessments', { params })
    .then(r => args.setAssessments(r.data))
    .catch(e => onLoadError(e, args.onUnauthorized))
    .finally(() => args.setLoading(false));
};

const patchLeadStatus = (id, status, onSuccess) =>
  apiClient.patch(`/admin/assessments/${id}/status`, { lead_status: status })
    .then(() => { toast.success('Lead status updated'); onSuccess(); })
    .catch(() => toast.error('Failed to update status'));

// ===== Hook =====

export function useAdminAssessments({ user, authLoading, onUnauthorized }) {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStability, setFilterStability] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);

  // Pure query-parameter bundle (no state setters) — keeps useMemo deps to 4.
  const queryArgs = useMemo(
    () => ({ search, filterStability, filterStatus, onUnauthorized }),
    [search, filterStability, filterStatus, onUnauthorized]
  );

  useEffect(() => {
    if (authLoading || !user) return;
    fetchAssessments({ ...queryArgs, setAssessments, setLoading });
  }, [authLoading, user, queryArgs, refreshTick, setAssessments, setLoading, fetchAssessments]);

  const refresh = useCallback(() => setRefreshTick(Date.now()), [setRefreshTick]);
  const updateStatus = useCallback(
    (id, status) => patchLeadStatus(id, status, refresh),
    [refresh, patchLeadStatus]
  );

  return {
    assessments,
    loading,
    search, setSearch,
    filterStability, setFilterStability,
    filterStatus, setFilterStatus,
    refresh,
    updateStatus,
  };
}
